import { setUser } from "@sentry/react";
import { Trans, useTranslation } from "react-i18next";

import { PortalHeading } from "@/components/Portal/PortalHeading/PortalHeading";
import { useGetCustomer, useGetCustomerDevices } from "@/services/api";

export const WelcomeText = () => {
  const { t } = useTranslation("portal");

  const { data: customer, isLoading: customerIsLoading } = useGetCustomer(
    undefined,
    {
      query: {
        onSuccess: (data) => {
          setUser({ id: data?.id?.toString() });
        },
        // Safe to cache for 5 minutes, as the name will never change.
        staleTime: 5 * 60 * 1000,
      },
    },
  );

  const { data: deviceSummary, isLoading: devicesLoading } =
    useGetCustomerDevices({
      query: {
        // Cache for 2 minutes - device counts are more dynamic than customer data
        staleTime: 2 * 60 * 1000,
      },
    });

  const customerName = customer?.firstName || "User";
  const isLoading = customerIsLoading || devicesLoading;
  const headingText = `${t("cockpit.heading.hi")} ${customerName}`;

  // Extract device counts with safe defaults
  const activeCount = deviceSummary?.activeDevices ?? 0;
  const noAutoRenewal = deviceSummary?.activeDevicesNoAR ?? 0;
  const suspendedCount = deviceSummary?.lockedDevices ?? 0;

  return (
    <PortalHeading isLoading={isLoading} heading={headingText}>
      <p>{t("cockpit.heading.welcomeText")}</p>
      <p className="min-h-[45px] xl:min-h-[unset]">
        <Trans
          t={t}
          values={{ count: activeCount }}
          components={[
            <span key="0" className="font-semibold text-primary-100" />,
          ]}
        >
          cockpit.heading.activeDeviceText
        </Trans>
        {(noAutoRenewal > 0 || isLoading) && (
          <>
            <span>
              {", "}
              <Trans
                t={t}
                values={{ count: noAutoRenewal }}
                components={[
                  <span key="0" className="font-semibold text-secondary-100" />,
                ]}
              >
                cockpit.heading.devicesWithoutRenewal
              </Trans>
            </span>
          </>
        )}

        <span>.</span>
      </p>
      {suspendedCount > 0 && (
        <p className="mt-4">
          <span className="font-semibold text-secondary-100">
            {t("cockpit.heading.suspendedDevice", { count: suspendedCount })}
          </span>
        </p>
      )}
    </PortalHeading>
  );
};
